import React from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor,
  useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronUp, ChevronDown } from "lucide-react";

/** Vertical sortable list: drag only from the grip handle, plus up/down fallback controls. */
export function SortableList({ ids, onReorder, children, disabled }) {
  const sensors = useSensors(
    // A small activation distance keeps taps on edit/delete/availability from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(active.id), to = ids.indexOf(over.id);
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(ids, from, to));
  };
  if (disabled) return children;
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>{children}</SortableContext>
    </DndContext>
  );
}

export function SortableRow({ id, index, total, onMove, handleLabel, testid, className = "", children, disabled }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });
  return (
    <div ref={setNodeRef} data-testid={testid}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`${className} ${isDragging ? "relative z-20 shadow-lg ring-2 ring-brand-red/30 bg-white rounded-lg" : ""}`}>
      {!disabled && (
        <div className="flex flex-col items-center shrink-0">
          <button ref={setActivatorNodeRef} {...attributes} {...listeners}
            data-testid={`${testid}-handle`} aria-label={handleLabel} title={handleLabel}
            className="p-1.5 -my-0.5 text-slate-300 hover:text-slate-600 cursor-grab active:cursor-grabbing touch-none">
            <GripVertical size={18} />
          </button>
          <div className="flex gap-0.5">
            <button data-testid={`${testid}-up`} aria-label="Pomeri gore" disabled={index === 0}
              onClick={() => onMove(index, index - 1)}
              className="p-1 text-slate-300 hover:text-slate-700 disabled:opacity-25 disabled:hover:text-slate-300"><ChevronUp size={14} /></button>
            <button data-testid={`${testid}-down`} aria-label="Pomeri dole" disabled={index === total - 1}
              onClick={() => onMove(index, index + 1)}
              className="p-1 text-slate-300 hover:text-slate-700 disabled:opacity-25 disabled:hover:text-slate-300"><ChevronDown size={14} /></button>
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

export { arrayMove };
